import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SeoConfig {
  title: string;
  description: string;
  noindex?: boolean;
}

const DEFAULT_SEO: SeoConfig = {
  title: 'Kerbly | Local laundry, on demand',
  description:
    'Kerbly helps customers book trusted local laundry operators in minutes.',
};

const DEFAULT_IMAGE_PATH = '/social-share-og.png';

function getSeoConfig(pathname: string): SeoConfig {
  if (pathname === '/') {
    return DEFAULT_SEO;
  }

  if (pathname === '/help') {
    return {
      title: 'Help & FAQ | Kerbly',
      description:
        'Get answers to common Kerbly operator questions about payouts, subscriptions, disputes, and support.',
    };
  }

  if (pathname === '/support') {
    return {
      title: 'Support | Kerbly',
      description:
        'Contact Kerbly support for help with bookings, payouts, accounts, or app access.',
    };
  }

  if (pathname === '/privacy') {
    return {
      title: 'Privacy Policy | Kerbly',
      description:
        'Read how Kerbly collects, uses, and protects customer and operator data across the platform.',
    };
  }

  if (pathname === '/terms') {
    return {
      title: 'Terms of Service | Kerbly',
      description:
        'Read the terms for using Kerbly as a customer or local operator, including bookings and payouts.',
    };
  }

  if (pathname === '/delete-account') {
    return {
      title: 'Delete Account | Kerbly',
      description:
        'Request deletion of your Kerbly account and associated personal data, including options when app access is unavailable.',
    };
  }

  if (pathname === '/login') {
    return {
      title: 'Sign in | Kerbly',
      description: 'Sign in to your Kerbly account to manage bookings, payouts, and settings.',
      noindex: true,
    };
  }

  if (pathname === '/register') {
    return {
      title: 'Become an operator | Kerbly',
      description:
        'Create your Kerbly operator account to start accepting local laundry bookings and earning income.',
      noindex: true,
    };
  }

  if (pathname === '/operators') {
    return {
      title: 'Become a Kerbly operator | Kerbly',
      description:
        'Start your local operator account on Kerbly and earn from laundry bookings in your suburb.',
    };
  }

  if (pathname === '/forgot') {
    return {
      title: 'Reset your password | Kerbly',
      description: 'Request a password reset link to regain access to your Kerbly account.',
      noindex: true,
    };
  }

  if (pathname.startsWith('/reset/')) {
    return {
      title: 'Choose a new password | Kerbly',
      description: 'Set a new password for your Kerbly account.',
      noindex: true,
    };
  }

  if (pathname.startsWith('/verify-email/')) {
    return {
      title: 'Verify your email | Kerbly',
      description: 'Confirm your email address to activate your Kerbly account.',
      noindex: true,
    };
  }

  if (
    pathname === '/onboarding' ||
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/admin')
  ) {
    return {
      title: 'Operator dashboard | Kerbly',
      description: 'Manage your Kerbly operator account, subscription, and payouts.',
      noindex: true,
    };
  }

  return {
    title: 'Kerbly',
    description: DEFAULT_SEO.description,
    noindex: true,
  };
}

function setOrCreateMeta(attr: 'name' | 'property', key: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const config = getSeoConfig(pathname);
    const origin = window.location.origin;
    const canonicalUrl = `${origin}${pathname}`;
    const imageUrl = `${origin}${DEFAULT_IMAGE_PATH}`;

    document.title = config.title;
    setCanonical(canonicalUrl);

    setOrCreateMeta('name', 'description', config.description);
    setOrCreateMeta('name', 'robots', config.noindex ? 'noindex, nofollow' : 'index, follow');

    setOrCreateMeta('property', 'og:type', 'website');
    setOrCreateMeta('property', 'og:site_name', 'Kerbly');
    setOrCreateMeta('property', 'og:title', config.title);
    setOrCreateMeta('property', 'og:description', config.description);
    setOrCreateMeta('property', 'og:url', canonicalUrl);
    setOrCreateMeta('property', 'og:image', imageUrl);
    setOrCreateMeta('property', 'og:image:alt', 'Kerbly logo');

    setOrCreateMeta('name', 'twitter:card', 'summary_large_image');
    setOrCreateMeta('name', 'twitter:title', config.title);
    setOrCreateMeta('name', 'twitter:description', config.description);
    setOrCreateMeta('name', 'twitter:image', imageUrl);
  }, [pathname]);

  return null;
}
